/**
 * Module Staff #10 — Utilisateurs & RBAC V2
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitres 8 et 9
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/admin/${name}`;

const moduleStaffRbac = {
  id: 'staff-users-rbac',
  population: 'staff',
  order: 10,
  category: 'Admin',
  title: 'Utilisateurs & RBAC V2',
  subtitle: 'Maîtriser les 14 rôles, les rôles personnalisés et l\'audit complet',
  icon: 'Shield',
  duration: '25 min',
  level: 'Avancé',
  audience: 'Administrateurs système, RSSI',
  slides: [
    { type: 'cover', eyebrow: 'Module 10 — Staff', title: 'RBAC V2', subtitle: 'Gouvernance fine des permissions internes Baobab', icon: 'Shield', duration: '25 min', audience: 'Admin IT, RSSI', level: 'Avancé' },
    { type: 'intro', title: 'Objectifs', intro: 'Le système RBAC V2 (Role-Based Access Control) gère 14 rôles natifs et permet la création de rôles personnalisés avec 160 permissions granulaires.', objectives: [
      'Comprendre les 14 rôles natifs (admin, CFO, opérateur, agent terrain...)',
      'Créer un compte utilisateur interne et lui attribuer un rôle',
      'Configurer un rôle personnalisé avec permissions sur mesure',
      'Auditer les actions utilisateurs (journal complet)',
      'Désactiver / réactiver un compte en respectant la traçabilité',
      'Gérer les sessions actives et forcer la déconnexion en cas d\'incident',
    ]},
    { type: 'screenshot', eyebrow: 'Centre utilisateurs', title: 'Gestion des Utilisateurs',
      image: IMG('img_admin_users.png'),
      callouts: [
        { label: 'Total + Actifs', text: 'Synthèse en haut : volumes en temps réel.', x: 75, y: 22 },
        { label: 'Recherche', text: 'Par nom, email — rapide même sur 1000+ comptes.', x: 30, y: 22 },
        { label: 'Type compte', text: 'Entreprise / particulier / interne — couleurs distinctives.', x: 70, y: 50 },
        { label: 'Actions', text: 'Désactiver / Modifier rôle / Forcer reset MDP.', x: 95, y: 50 },
      ],
    },
    { type: 'concept', eyebrow: 'Vocabulaire', icon: 'Users', title: 'Les 14 rôles natifs',
      lede: 'Le système livre 14 rôles prédéfinis qui couvrent 95 % des besoins :',
      bullets: [
        '👑 super_admin · admin · cfo · finance_manager (direction & finances)',
        '⚙️ operations_manager · operator · dispatch · agent_terrain (opérations)',
        '💼 commercial · customer_success · sales (commercial & relation client)',
        '🔧 it_admin · auditor · observer (technique & audit)',
        '🚚 chauffeur (rôle pour app mobile chauffeur, hors back-office)',
      ],
      note: 'Pour des besoins spécifiques, créez un rôle personnalisé via Configuration → RBAC → Rôles custom.',
    },
    { type: 'steps', title: 'Créer un utilisateur interne avec rôle', steps: [
      { title: 'Bouton « + Nouvel utilisateur »', detail: 'En haut à droite de Gestion Utilisateurs.' },
      { title: 'Saisir les infos basiques', detail: 'Nom, email pro, téléphone — pas de doublon possible.' },
      { title: 'Sélectionner le rôle', detail: 'Liste déroulante des 14 rôles natifs + rôles custom.' },
      { title: 'Définir un mot de passe initial', detail: 'Forcer le changement à la 1ère connexion.' },
      { title: 'Activer la 2FA obligatoire', detail: 'Pour les rôles sensibles (CFO, admin) c\'est non-négociable.' },
      { title: 'Valider', detail: 'L\'utilisateur reçoit un email d\'activation immédiatement.' },
    ]},
    { type: 'steps', title: 'Créer un rôle personnalisé (RBAC V2)', steps: [
      { title: 'Configuration → Rôles → Nouveau', detail: 'Nécessite le rôle super_admin.' },
      { title: 'Nommer le rôle', detail: 'Ex: "Comptable Junior", "Auditeur externe"...' },
      { title: 'Sélectionner les permissions parmi 160', detail: 'Granularité par module (Opérations, Finances, Clients, Admin...).' },
      { title: 'Tester avec un compte de test', detail: 'Important : vérifier ce que l\'utilisateur voit/peut faire.' },
      { title: 'Assigner à des utilisateurs', detail: 'Le rôle apparaît désormais dans la liste déroulante.' },
    ]},
    { type: 'comparison', title: 'Gouvernance RBAC', do: [
      'Principe de moindre privilège (donner le minimum nécessaire)',
      'Auditer les accès tous les trimestres',
      'Forcer la 2FA pour tous les rôles sensibles',
      'Désactiver immédiatement les comptes inactifs > 90 jours',
      'Documenter les rôles custom dans un fichier de gouvernance',
    ], dont: [
      'Donner super_admin "pour ne pas avoir de problème"',
      'Réutiliser les comptes entre collaborateurs',
      'Supprimer les comptes au lieu de les désactiver',
      'Créer des rôles custom sans tester',
      'Ignorer les alertes de sessions suspectes',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Un comptable junior arrive. Quel rôle lui attribuer ?',
      options: [
        'super_admin pour qu\'il puisse "tout faire"',
        'cfo pour qu\'il puisse voir les KPIs',
        'auditor (lecture seule sur toutes les données financières)',
        'Créer un rôle custom "Comptable Junior" avec uniquement les permissions nécessaires',
      ],
      answerIndex: 3,
      explanation: 'Le principe de moindre privilège recommande un rôle dédié. auditor est trop large, cfo donne des accès trop élevés (modification objectifs, etc.).',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Un CFO part en congé. Que faire de son compte ?',
      options: [
        'Le supprimer pour sécurité',
        'Désactiver temporairement + forcer la déconnexion des sessions',
        'Lui demander son mot de passe',
        'Laisser actif "au cas où il faille intervenir"',
      ],
      answerIndex: 1,
      explanation: 'Désactivation temporaire = sécurité + traçabilité. À la reprise, réactivation immédiate possible. Ne JAMAIS demander le mot de passe.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      'Principe de moindre privilège, toujours.',
      '14 rôles natifs + rôles custom si besoins fins.',
      '2FA obligatoire sur les rôles sensibles.',
      'Désactiver ≠ supprimer (audit OHADA).',
      'Audit trimestriel des accès = bonne hygiène RSSI.',
    ]},
    { type: 'cta', title: 'Module 10 terminé !', subtitle: 'Vous maîtrisez la gouvernance des accès.',
      actions: [
        { label: 'Gestion utilisateurs', href: '/admin/users' },
        { label: 'Configuration RBAC', href: '/admin/settings' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffRbac;
